import { useState, useEffect, useMemo } from 'react'
import { View, Text, Pressable, Modal, ScrollView, StyleSheet } from 'react-native'
import { useTranslation } from 'react-i18next'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'
import { colors, fonts } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAvatars } from '../../lib/useAvatars'
import PersonAvatarFace from '../PersonAvatarFace'
import { stripTeamBracket } from '../Pick/ui'
import RiderRow from './RiderRow'
import { loadRiderInfo } from '../Pick/riderInfo'
import { fetchOdrResults, fetchStageResults, makeEffective, oneDayScore, fmtGap, OUT_OF_RANK } from '../Pick/odrResults'

// Dettaglio della cinquina di un giocatore dalla Classifica. Stesso disegno della scheda giocatore della pagina Pick
// (volto, nome, una riga per scelta con maglia della squadra e bandiera), con in piu' il risultato di ogni scelta:
// gara di un giorno = piazzamento e punti (con bonus/malus e totale), tappa di un giro = piazzamento e distacco.
// player: { id, name, pos?, value? } (pos e value sono quelli mostrati nella classifica, per la riga sotto il nome)
export default function PickModal({ player, idStage, leagueId, raceId, isOneDay, onClose }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const insets = useSafeAreaInsets()
  const { user } = useAuth()
  const avatars = useAvatars([player.id])
  const [data, setData] = useState(null)      // null = in caricamento
  const isMe = user?.id === player.id

  useEffect(() => {
    if (!player?.id || !idStage || !leagueId) return
    let cancelled = false
    ;(async () => {
      const [pRes, fRes, cRes] = await Promise.all([
        supabase.from('fact_user_pick').select('id_user, id_slot, id_rider').eq('id_stage', idStage).eq('id_league', leagueId),
        raceId
          ? supabase.from('fact_user_flip').select('id_rider, id_slot').eq('id_user', player.id).eq('id_race', raceId).eq('id_league', leagueId).eq('id_stage_used', idStage).maybeSingle()
          : Promise.resolve({ data: null }),
        raceId ? supabase.from('dim_race').select('id_race_category').eq('id_race', raceId).maybeSingle() : Promise.resolve({ data: null }),
      ])
      if (cancelled) return
      const all = pRes.data ?? []
      const mine = {}; all.filter(r => r.id_user === player.id).forEach(r => { mine[r.id_slot] = r.id_rider })
      const myKeys = new Set(all.filter(r => r.id_user === user?.id).map(r => r.id_rider))
      const counts = {}; all.forEach(r => { counts[r.id_rider] = (counts[r.id_rider] ?? 0) + 1 })
      const totalPlayers = new Set(all.map(r => r.id_user)).size
      const flip = fRes.data ?? null
      const ids = [...new Set([...Object.values(mine), flip?.id_rider])].filter(Boolean)
      const [riders, res] = await Promise.all([
        loadRiderInfo(ids),
        isOneDay ? fetchOdrResults(idStage, ids) : fetchStageResults(idStage, ids),
      ])
      if (cancelled) return

      const slots = Object.keys(mine).map(Number).sort((a, b) => a - b)
      let rows, summary = null
      if (isOneDay) {
        // come view_effective_picks: il flip sostituisce il titolare solo se arriva meglio
        const eff = makeEffective({ userRiders: { [player.id]: mine } }, flip ? { [player.id]: { id_slot: flip.id_slot, id_rider: flip.id_rider } } : {}, res.ranks)
        rows = slots.map(sl => {
          const id = eff({ id_user: player.id }, sl)
          const rank = res.ranks[id] ?? null
          return { slot: sl, id, replaced: id !== mine[sl], rank, pts: rank != null ? (res.scores[sl]?.[rank] ?? OUT_OF_RANK) : null }
        })
        const scored = rows.filter(r => r.rank != null)
        if (scored.length) summary = oneDayScore(scored)
      } else {
        rows = slots.map(sl => {
          const id = mine[sl]
          const r = res.results[id]
          return { slot: sl, id, rank: r?.rank ?? null, gap: r?.gap ?? null, dnf: !!r?.dnf }
        })
      }
      rows.forEach(r => {
        r.count = counts[r.id] ?? 0
        r.unique = r.count === 1
        r.shared = !isMe && myKeys.has(r.id)
      })
      setData({ rows, riders, flip, flipRider: flip ? riders[flip.id_rider] : null, national: cRes.data?.id_race_category === 5, totalPlayers, summary })
    })().catch(() => { if (!cancelled) setData({ rows: [], riders: {}, error: true }) })
    return () => { cancelled = true }
  }, [player?.id, idStage, leagueId, raceId, isOneDay, user?.id, isMe])

  const nameOf = (rd, fallback) => rd ? (data?.national ? stripTeamBracket(rd.labelNew || rd.label) : (rd.labelNew || rd.label)) : fallback

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} accessibilityRole="button" accessibilityLabel={t('pick.close')} />
        <View style={[styles.sheet, { backgroundColor: tc.bgCard, paddingBottom: insets.bottom + 12 }]}>
          <View style={[styles.header, { borderBottomColor: tc.border }]}>
            <PersonAvatarFace userId={player.id} settings={avatars[player.id]} size={44} />
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={[styles.title, { color: isMe ? tc.rosaText : tc.text }]} numberOfLines={1}>{player.name}</Text>
              {(player.pos || player.value) ? (
                <Text style={[styles.sub, { color: tc.textMuted }]} numberOfLines={1}>
                  {[player.pos ? `${player.pos}°` : null, player.value].filter(Boolean).join(' · ')}
                </Text>
              ) : null}
            </View>
            <Pressable onPress={onClose} hitSlop={6} style={[styles.close, { backgroundColor: tc.bgCard2 }]} accessibilityRole="button" accessibilityLabel={t('pick.close')}>
              <MaterialCommunityIcons name="close" size={18} color={tc.text} />
            </Pressable>
          </View>

          <ScrollView contentContainerStyle={{ padding: 12, gap: 8 }}>
            {data === null ? (
              <Text style={[styles.empty, { color: tc.textMuted }]}>{t('common.loading')}</Text>
            ) : data.rows.length === 0 ? (
              <Text style={[styles.empty, { color: tc.textMuted }]}>{t('pick.no_pick_stage')}</Text>
            ) : (
              <>
                {data.rows.map(r => {
                  const rd = data.riders[r.id]
                  return (
                    <RiderRow
                      key={r.slot}
                      slot={r.slot}
                      rd={rd}
                      national={data.national}
                      name={nameOf(rd, '—')}
                      highlight={r.shared}
                      tags={(
                        <View style={styles.tags}>
                          <Text style={[styles.count, { color: tc.textMuted }]}>{t('pick.sheet_chosen_by', { count: r.count, total: data.totalPlayers })}</Text>
                          {r.shared && (
                            <View style={styles.tag}>
                              <MaterialCommunityIcons name="account-check-outline" size={12} color={tc.rosaText} />
                              <Text style={[styles.tagText, { color: tc.rosaText }]}>{t('pick.sheet_shared')}</Text>
                            </View>
                          )}
                          {r.unique && (
                            <View style={styles.tag}>
                              <MaterialCommunityIcons name="star-four-points-outline" size={12} color={tc.rosaText} />
                              <Text style={[styles.tagText, { color: tc.rosaText }]}>{t('pick.sheet_unique')}</Text>
                            </View>
                          )}
                          {r.replaced && (
                            <View style={styles.tag}>
                              <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
                            </View>
                          )}
                        </View>
                      )}
                      right={(
                        <View style={styles.result}>
                          {r.rank != null ? (
                            <>
                              <Text style={[styles.rank, { color: tc.text }]}>{r.rank}°</Text>
                              {isOneDay
                                ? <Text style={[styles.pts, { color: r.pts > 0 ? colors.success : tc.textMuted }]}>{r.pts > 0 ? `+${r.pts}` : r.pts} pt</Text>
                                : !!fmtGap(r.gap) && <Text style={[styles.gap, { color: tc.textMuted }]}>{fmtGap(r.gap)}</Text>}
                            </>
                          ) : (
                            <Text style={[styles.gap, { color: tc.textMuted }]}>{r.dnf ? t('pick.result_dnf') : '—'}</Text>
                          )}
                        </View>
                      )}
                    />
                  )
                })}

                {/* Gara di un giorno: bonus/malus e totale (le stesse regole di fn_simulazione_punti) */}
                {data.summary && (
                  <View style={[styles.total, { borderColor: tc.border, backgroundColor: tc.bgCard2 }]}>
                    {data.summary.bonus.map(b => (
                      <View key={b.kind} style={styles.bonusRow}>
                        <Text style={[styles.bonusLabel, { color: tc.textMuted }]}>
                          {b.kind === 'malus' ? t('pick.result_malus') : b.kind === 'top10' ? t('pick.result_bonus_top10') : t('pick.result_bonus_top15')}
                        </Text>
                        <Text style={[styles.bonusVal, { color: b.value > 0 ? colors.success : tc.textMuted }]}>{b.value > 0 ? `+${b.value}` : b.value} pt</Text>
                      </View>
                    ))}
                    <View style={styles.bonusRow}>
                      <Text style={[styles.totalLabel, { color: tc.text }]}>{t('leaderboard.sheet_total')}</Text>
                      <Text style={[styles.totalVal, { color: tc.rosaText }]}>{data.summary.total} pt</Text>
                    </View>
                  </View>
                )}

                {data.flip && data.flipRider && (
                  <View style={styles.flipRow}>
                    <MaterialCommunityIcons name="swap-horizontal" size={12} color={colors.flip} />
                    <Text style={styles.flipText}>{nameOf(data.flipRider, '')}</Text>
                    <Text style={[styles.flipSlot, { color: tc.textMuted }]}>{t('pick.flip_slot_arrow', { slot: data.flip.id_slot })}</Text>
                  </View>
                )}
              </>
            )}
          </ScrollView>
        </View>
      </View>
    </Modal>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.6)', justifyContent: 'flex-end' },
  sheet: { borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: '82%' },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, padding: 14, borderBottomWidth: 0.5 },
  title: { fontFamily: fonts.condensedBold, fontSize: 20 },
  sub: { fontSize: 12, marginTop: 1 },
  close: { width: 32, height: 32, borderRadius: 16, alignItems: 'center', justifyContent: 'center' },
  empty: { fontSize: 13, textAlign: 'center', paddingVertical: 24 },
  tags: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 3 },
  count: { fontSize: 11 },
  tag: { flexDirection: 'row', alignItems: 'center', gap: 3 },
  tagText: { fontSize: 11, fontFamily: fonts.condensedBold },
  result: { alignItems: 'flex-end', minWidth: 54 },
  rank: { fontFamily: fonts.condensedBold, fontSize: 18 },
  pts: { fontFamily: fonts.condensedBold, fontSize: 12 },
  gap: { fontSize: 11 },
  total: { borderWidth: 1, borderRadius: 12, padding: 12, gap: 6, marginTop: 4 },
  bonusRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  bonusLabel: { fontSize: 12 },
  bonusVal: { fontFamily: fonts.condensedBold, fontSize: 13 },
  totalLabel: { fontFamily: fonts.condensedBold, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.5 },
  totalVal: { fontFamily: fonts.condensedBold, fontSize: 20 },
  flipRow: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 3 },
  flipText: { fontSize: 11, color: colors.flip, fontFamily: fonts.condensedBold, textTransform: 'uppercase' },
  flipSlot: { fontSize: 10 },
})
